import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, AlertTriangle } from "lucide-react";
import { useAuth } from "@/auth";
import { apiFetch } from "@/api";
import { Button, Spinner } from "@/ui";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) {
      setState("error");
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const d = await apiFetch(`/payments/status/${sid}`);
        if (d.payment_status === "paid") {
          await refresh();
          if (!cancelled) {
            setState("paid");
            setTimeout(() => navigate("/home"), 1400);
          }
          return;
        }
        if (["failed", "expired"].includes(d.payment_status)) {
          if (!cancelled) setState("error");
          return;
        }
      } catch {
        /* keep polling */
      }
      tries += 1;
      if (tries >= 12) {
        if (!cancelled) setState("pending");
        return;
      }
      if (!cancelled) setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-6 text-center">
      <div className="w-full max-w-md flex flex-col items-center gap-5">
        {state === "checking" ? (
          <>
            <Spinner className="h-7 w-7 text-foreground" />
            <h1 data-testid="payment-checking" className="text-2xl font-bold">Confirming your payment…</h1>
            <p className="text-muted-foreground">This only takes a moment.</p>
          </>
        ) : null}
        {state === "paid" ? (
          <>
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-foreground text-background"><Check className="h-8 w-8" /></div>
            <h1 data-testid="payment-success" className="text-3xl font-bold">You're all set.</h1>
            <p className="text-muted-foreground">Welcome to MAXx Premium. Taking you back to MAXx…</p>
          </>
        ) : null}
        {state === "pending" ? (
          <>
            <h1 data-testid="payment-pending" className="text-2xl font-bold">Still processing…</h1>
            <p className="text-muted-foreground">Your payment is taking a little longer than usual. It'll unlock automatically once confirmed.</p>
            <Button data-testid="go-to-chat" onClick={() => navigate("/home")}>Go to MAXx</Button>
          </>
        ) : null}
        {state === "error" ? (
          <>
            <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-foreground"><AlertTriangle className="h-8 w-8" /></div>
            <h1 data-testid="payment-error" className="text-2xl font-bold">We couldn't confirm that payment.</h1>
            <p className="text-muted-foreground">No charge was completed. You can try again.</p>
            <Button data-testid="back-to-paywall" onClick={() => navigate("/paywall")}>Back to subscribe</Button>
          </>
        ) : null}
      </div>
    </div>
  );
}
